import { InvoiceData } from '../../services/pdf.service';
import {
  isPaymentTransactionAmountInclusiveOfAddons,
  sumPaymentTransactionAddonAmount,
} from '../../services/payment.service';
import { PaymentMethodCode } from '../types/payment-method.enum';
import { PhoneValidator } from '../validators/phone.validator';
import { formatDateTimeInTimeZone, getUserTimezone } from './date.util';
import {
  formatCardPaymentLabel,
  isCardPaymentMethod,
  resolvePaymentReceiptCard,
} from './card-payment-label.util';
import { parsePromoMessageLines } from './receipt-promo-message.util';
import { RAINCHECK_MESSAGE, shouldShowRaincheckMessage } from './raincheck-policy.util';

/**
 * ESC/POS receipt encoder.
 *
 * Produces the raw byte payload for a thermal receipt printer, mirroring the layout of the
 * PDF receipt in {@link PdfService.printInvoice} (business header, INVOICE, order/date,
 * BILL TO, itemised table, TOTAL, payment, footer). The bytes are sent to the printer by the
 * Android WebView native bridge over a raw TCP socket — browsers cannot open raw sockets.
 */

// ── ESC/POS control codes ────────────────────────────────────────────────────
const ESC = 0x1b;
const GS = 0x1d;

const CMD = {
  init: [ESC, 0x40], // ESC @  — reset printer to defaults
  alignLeft: [ESC, 0x61, 0x00],
  alignCenter: [ESC, 0x61, 0x01],
  boldOn: [ESC, 0x45, 0x01],
  boldOff: [ESC, 0x45, 0x00],
  sizeNormal: [GS, 0x21, 0x00], // GS ! 0
  sizeDoubleHeight: [GS, 0x21, 0x01],
  sizeDouble: [GS, 0x21, 0x11], // double width + double height
  openCashDrawer: [ESC, 0x70, 0x00, 0x19, 0xfa], // ESC p 0 25 250 — pulse drawer pin 2
  cutPartial: [GS, 0x56, 0x01], // GS V 1 — partial cut
} as const;

/**
 * Blank lines fed after the footer before cutting. The print head sits above the cutter, so the
 * paper must advance far enough for the last printed line to clear the blade — otherwise the footer
 * is sheared off and bleeds onto the top of the next receipt.
 */
const TAIL_FEED_LINES = 6;

/** Accumulates ESC/POS bytes with small text/formatting helpers. */
class EscPosBuilder {
  private readonly bytes: number[] = [];

  constructor(private readonly charsPerLine: number) {}

  raw(cmd: readonly number[]): this {
    for (const b of cmd) this.bytes.push(b & 0xff);
    return this;
  }

  /** Appends Latin-1 encoded text (thermal printers use single-byte code pages). */
  text(value: string): this {
    for (let i = 0; i < value.length; i += 1) {
      this.bytes.push(value.charCodeAt(i) & 0xff);
    }
    return this;
  }

  line(value = ''): this {
    return this.text(value).text('\n');
  }

  /** Advances the paper by [lines] blank lines (ESC d n). */
  feed(lines: number): this {
    const n = Math.max(0, Math.min(255, Math.floor(lines)));
    return this.raw([ESC, 0x64, n]);
  }

  /** Full-width divider matching the horizontal rules on the PDF receipt. */
  divider(): this {
    return this.line('-'.repeat(this.charsPerLine));
  }

  /** Left label + right-aligned amount on one line (truncates the label if needed). */
  twoColumns(left: string, right: string): this {
    const maxLeft = Math.max(0, this.charsPerLine - right.length - 1);
    let label = left;
    if (label.length > maxLeft) {
      const marker = maxLeft >= 3 ? '...' : '';
      label = label.slice(0, Math.max(0, maxLeft - marker.length)).trimEnd() + marker;
    }
    const gap = Math.max(1, this.charsPerLine - label.length - right.length);
    return this.line(label + ' '.repeat(gap) + right);
  }

  build(): Uint8Array {
    return Uint8Array.from(this.bytes);
  }
}

const money = (value: number): string => `$${Math.max(0, value).toFixed(2)}`;

/**
 * Greedy word wrap for fixed-width receipt lines. The printer hard-wraps anything longer
 * than the column count mid-word, which reads as a typo on a customer-facing sentence.
 */
function wrapToWidth(value: string, width: number): string[] {
  const words = value.split(/\s+/).filter(word => word !== '');
  if (words.length === 0) return [];

  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (current === '') {
      current = word;
    } else if (current.length + 1 + word.length <= width) {
      current += ` ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  }
  lines.push(current);
  return lines;
}

const clean = (value: string | null | undefined, fallback = ''): string =>
  value != null && String(value).trim() !== '' ? String(value).trim() : fallback;

function resolveReceiptTimezone(invoiceData: InvoiceData): string {
  return (
    invoiceData.receiptTimezone?.trim() ||
    invoiceData.order.locationTimezone?.trim() ||
    getUserTimezone()
  );
}

function formatReceiptDateTime(dateString: string | null | undefined, timeZone: string): string {
  if (!dateString) return 'N/A';
  const formatted = formatDateTimeInTimeZone(dateString, timeZone);
  return formatted || 'N/A';
}

function formatPaymentMethod(paymentMethod: string): string {
  if (!paymentMethod) return '-';
  if (paymentMethod.toLowerCase() === PaymentMethodCode.CardPresent) return 'Card';
  return paymentMethod
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Order-level cardDetails apply only to card-like transactions (not cash / membership). */
function resolveReceiptCardDetails(
  tx: InvoiceData['transaction'],
  orderCardDetails: InvoiceData['cardDetails']
): { brand?: string | null; last4?: string | null } | null {
  if (!isCardPaymentMethod(tx.payment_method)) {
    return null;
  }
  return resolvePaymentReceiptCard(tx, orderCardDetails);
}

/**
 * Builds the raw ESC/POS byte payload for a receipt from the same {@link InvoiceData} used by
 * the PDF path, so the printed output matches the downloadable invoice.
 */
export function buildEscPosReceipt(invoiceData: InvoiceData, charsPerLine: number): Uint8Array {
  const b = new EscPosBuilder(charsPerLine > 0 ? charsPerLine : 48);
  const tx = invoiceData.transaction;

  // Amounts — reconciled exactly like the PDF receipt.
  const baseAmount = isFinite(Number(tx.amount)) ? Number(tx.amount) : 0;
  const addonRows = Array.isArray(tx.addons) ? tx.addons : [];
  const addonSum = sumPaymentTransactionAddonAmount(tx);
  const amountIncludesAddons = isPaymentTransactionAmountInclusiveOfAddons(tx);
  const packagePortion = amountIncludesAddons
    ? Math.max(0, baseAmount - addonSum)
    : Math.max(0, baseAmount);
  const subtotalBeforeDiscount = amountIncludesAddons ? baseAmount : baseAmount + addonSum;
  const discountNum = Number(tx.discount || 0);
  const totalAmount = Math.max(0, subtotalBeforeDiscount - discountNum);

  const businessName = clean(invoiceData.businessInfo.name, 'Car Wash');

  b.raw(CMD.init);

  // Business header
  b.raw(CMD.alignCenter).raw(CMD.boldOn).raw(CMD.sizeDouble).line(businessName);
  b.raw(CMD.sizeNormal).raw(CMD.boldOff);
  b.line(); // breathing room between the shop name and its contact details
  const phone = PhoneValidator.formatUsaPhone(clean(invoiceData?.businessInfo?.phone ?? ''));
  const email = clean(invoiceData.businessInfo.email);
  if (phone) b.line(phone);
  if (email) b.line(email);

  b.raw(CMD.alignLeft).divider();

  // Receipt title
  b.raw(CMD.alignCenter).raw(CMD.boldOn).line('RECEIPT').raw(CMD.boldOff).raw(CMD.alignLeft);

  const rawOrder = invoiceData.order as any as Record<string, any>;
  const orderId = clean(
    (invoiceData.order.orderId as string) ||
      (rawOrder['order_id'] as string) ||
      (rawOrder['id'] as string),
    'N/A'
  ).replace(/^#+/, '');
  const receiptTimezone = resolveReceiptTimezone(invoiceData);
  b.twoColumns(
    `Order: #${orderId}`,
    `Date: ${formatReceiptDateTime(tx.payment_date, receiptTimezone)}`
  );

  b.divider();

  // Bill to
  b.raw(CMD.boldOn).line('BILL TO:').raw(CMD.boldOff);
  const billLines = [invoiceData.customerInfo.name, invoiceData.customerInfo.email].filter(
    v => v && v.trim() !== '' && v.trim() !== 'N/A'
  );
  for (const l of billLines) b.line(l);

  b.divider();

  // Items table
  b.raw(CMD.boldOn).twoColumns('DESCRIPTION', 'AMOUNT').raw(CMD.boldOff);
  b.divider();

  b.twoColumns(clean(tx.package_name, 'Car Wash Service'), money(packagePortion));

  for (const a of addonRows) {
    const count = Math.max(1, Number(a.add_on_count || 1));
    const price = Number(a.price ?? 0);
    const lineTotal = isFinite(price) && isFinite(count) ? price * count : 0;
    const name = clean(a.addon_name, 'Add-on') || 'Add-on';
    const label = count > 1 ? `Add-on: ${name} x${count}` : `Add-on: ${name}`;
    b.twoColumns(label, money(lineTotal));
  }

  if (discountNum > 0) {
    const dLabel = tx.voucher_code ? `Discount (${tx.voucher_code})` : 'Discount';
    b.twoColumns(dLabel, `-${money(discountNum)}`);
  }

  b.twoColumns('Taxes + Fees', '$0.00');

  b.divider();

  // Total
  b.raw(CMD.boldOn).raw(CMD.sizeDoubleHeight).twoColumns('TOTAL', money(totalAmount));
  b.raw(CMD.sizeNormal).raw(CMD.boldOff);

  const promoLines = parsePromoMessageLines(tx.promo_message);
  if (promoLines.length > 0) {
    const width = charsPerLine > 0 ? charsPerLine : 48;
    b.line('-'.repeat(width));
    // Each configured line is bold + center-aligned (empty lines keep vertical spacing).
    b.raw(CMD.alignCenter).raw(CMD.boldOn);
    for (const line of promoLines) {
      b.line(line);
    }
    b.raw(CMD.boldOff).raw(CMD.alignLeft);
    b.line('-'.repeat(width));
  }

  // Payment method
  if (tx.payment_method && tx.payment_method !== '-') {
    const cardSource = resolveReceiptCardDetails(tx, invoiceData.cardDetails);
    const cardLabel = formatCardPaymentLabel(cardSource?.brand, cardSource?.last4)?.replace(
      /•/g,
      '*'
    );
    const pm = cardLabel || formatPaymentMethod(tx.payment_method);
    b.line(`Payment: ${pm}`);
  }

  b.divider();

  // Footer
  b.raw(CMD.alignCenter);
  // 24-hour raincheck promise — single wash purchases only (CWS-1523).
  if (shouldShowRaincheckMessage(invoiceData)) {
    b.raw(CMD.boldOn);
    for (const line of wrapToWidth(RAINCHECK_MESSAGE, charsPerLine > 0 ? charsPerLine : 48)) {
      b.line(line);
    }
    b.raw(CMD.boldOff);
  }
  b.line(`Drive clean! - ${businessName}`);
  if (email) b.line(email);
  b.raw(CMD.alignLeft);

  b.feed(TAIL_FEED_LINES).raw(CMD.cutPartial);

  return b.build();
}

/**
 * ESC/POS payload that only opens the cash drawer (no receipt content).
 * Init (ESC @) then drawer kick (ESC p 0 25 250) — same pulse formerly embedded in cash receipts.
 */
export function buildEscPosOpenDrawer(): Uint8Array {
  const bytes = new Uint8Array(CMD.init.length + CMD.openCashDrawer.length);
  bytes.set(CMD.init, 0);
  bytes.set(CMD.openCashDrawer, CMD.init.length);
  return bytes;
}

/** Base64-encodes ESC/POS bytes for transport across the WebView JavaScript bridge. */
export function escPosToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk) as number[]);
  }
  return btoa(binary);
}
